/**
 * Carnival decorative treatment — inline SVG confetti, ribbon divider,
 * and corner gradient washes. All hand-optimized, aria-hidden, and
 * built to layer under text without hurting readability.
 *
 * Colour palette: extended from the existing site's #0AADA0 teal and
 * #E63946 coral with two additional Carnival-warm accents (gold
 * #F1B500, purple #7B4B94) — no new palette, just additional tokens
 * used only inside decorative SVGs.
 *
 * Usage:
 *   <div className="relative">
 *     <ConfettiScatter />
 *     <CornerGradient position="top-right" />
 *     ... your content ...
 *   </div>
 */

const CONFETTI_COLORS = ["#0AADA0", "#E63946", "#F1B500", "#7B4B94"];

// Pseudo-random but stable across renders — indexed by (seed, i) so
// the pattern is deterministic. Kept ~20 dots to stay light.
const CONFETTI_DOTS = Array.from({ length: 22 }, (_, i) => {
  // Simple LCG to avoid Math.random() causing hydration mismatches.
  const rand = (seed) => {
    let s = (seed * 9301 + 49297) % 233280;
    return s / 233280;
  };
  return {
    cx: rand(i * 7 + 3) * 100,
    cy: rand(i * 13 + 5) * 100,
    r: 0.6 + rand(i * 17 + 11) * 1.4,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    rotate: rand(i * 23 + 19) * 360,
  };
});

export const ConfettiScatter = ({ className = "" }) => (
  <svg
    aria-hidden="true"
    className={`pointer-events-none absolute inset-0 w-full h-full ${className}`}
    viewBox="0 0 100 100"
    preserveAspectRatio="none"
    style={{ opacity: 0.45 }}
  >
    {CONFETTI_DOTS.map((d, i) => (
      <g key={i} transform={`rotate(${d.rotate} ${d.cx} ${d.cy})`}>
        <rect
          x={d.cx - d.r}
          y={d.cy - d.r * 0.4}
          width={d.r * 2}
          height={d.r * 0.8}
          rx={d.r * 0.2}
          fill={d.color}
        />
      </g>
    ))}
  </svg>
);

export const RibbonDivider = ({ className = "" }) => (
  <div className={`relative w-full my-6 ${className}`} aria-hidden="true">
    <svg
      viewBox="0 0 800 24"
      preserveAspectRatio="none"
      className="w-full h-6"
    >
      <defs>
        <linearGradient id="ribbon-grad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#0AADA0" />
          <stop offset="33%" stopColor="#F1B500" />
          <stop offset="66%" stopColor="#E63946" />
          <stop offset="100%" stopColor="#7B4B94" />
        </linearGradient>
      </defs>
      <path
        d="M0 12 Q 100 2 200 12 T 400 12 T 600 12 T 800 12"
        fill="none"
        stroke="url(#ribbon-grad)"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.55"
      />
    </svg>
  </div>
);

/**
 * Soft radial-gradient wash in one corner. Provides warmth without
 * requiring a photo. Rendered via CSS radial-gradient inside a
 * pointer-events-none div, so no images loaded.
 */
export const CornerGradient = ({ position = "top-right", className = "" }) => {
  const positions = {
    "top-left": "top-0 left-0",
    "top-right": "top-0 right-0",
    "bottom-left": "bottom-0 left-0",
    "bottom-right": "bottom-0 right-0",
  };
  const gradients = {
    "top-left": "radial-gradient(circle at 0% 0%, rgba(10,173,160,0.14), transparent 55%)",
    "top-right": "radial-gradient(circle at 100% 0%, rgba(241,181,0,0.16), transparent 55%)",
    "bottom-left": "radial-gradient(circle at 0% 100%, rgba(123,75,148,0.13), transparent 55%)",
    "bottom-right": "radial-gradient(circle at 100% 100%, rgba(230,57,70,0.14), transparent 55%)",
  };
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute ${positions[position]} w-1/2 h-1/2 ${className}`}
      style={{ background: gradients[position] }}
    />
  );
};

/**
 * Complete decorative wrapper for a Carnival hub hero region:
 * confetti + all four corner gradients + border.
 */
export const CarnivalHeroDecor = () => (
  <>
    <ConfettiScatter className="opacity-60" />
    <CornerGradient position="top-left" />
    <CornerGradient position="top-right" />
    <CornerGradient position="bottom-left" />
    <CornerGradient position="bottom-right" />
  </>
);
